<template>
  <div id="app">
    <section
      :class="{
        'is-mobile': isMobile,
        'no-header': !renderHeader,
        'no-nav': !renderNav,
      }"
      :style="{
        '--nav-max-width': pinned && !isMobile ? '90px' : '230px',
      }"
      class="layout"
    >
      <!-- top bar -->
      <template v-if="renderHeader">
        <the-logo
          v-if="!isMobile || (isMobile && retractNavigation)"
          :class="{
            'mouseover-pin': mouseOverPinState,
          }"
          class="layout__logo"
          @click.native="retract"
        />
        <the-user
          class="layout__user"
          @toggle="handlerToggleRetractNavigation"
        />
      </template>

      <!-- side bar -->
      <template v-if="renderNav">
        <the-navigation
          v-if="!isMobile || (isMobile && retractNavigation)"
          :class="{
            expand: pinned && !minimized && !isMobile,
            'mouseover-pin': mouseOverPinState,
          }"
          class="layout__nav"
          @mouseover="mouseOverRetract"
        />
      </template>

      <!-- retract button -->
      <template v-if="!isMobile && renderNav">
        <button
          :class="{
            rotate: !minimized && !mouseover,
            expand: pinned && !minimized && !isMobile,
            'mouseover-pin': mouseOverPinState,
          }"
          class="retract"
          @[mouseenter]="mouseOverPin(true)"
          @[mouseleave]="mouseOverPin(false)"
          @click="retract"
        >
          <i class="fas fa-chevron-right" />
        </button>
      </template>

      <!-- content -->
      <router-view
        v-if="bootstrapped"
        class="layout__content"
        :class="{ darkenContent: retractNavigation }"
      />
    </section>

    <loader
      v-if="loading"
      :object="loaderConfig.color.light"
      :color1="loaderConfig.color.secondary"
      :color2="loaderConfig.color.primary"
      :opacity="loaderConfig.opacity"
      :bg="loaderConfig.backgroundColor"
      :size="loaderConfig.size"
      :speed="loaderConfig.speed"
      :name="loaderConfig.name"
      :objectbg="loaderConfig.objectbg"
    />

    <TheNoticeVersion v-if="noticeVersion" />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

import useTheme from '@/composables/theme';
import { useAppVersion } from '@/composables/useAppVersion';

import loadingMixin from '@/mixins/loadingView.mixin';
import verifyDataUserMixin from '@/mixins/verifyDataUser.mixin';

import themeFallback from '@/data/theme.json';

import TheLogo from '@/components/TheLogo.vue';
import TheUser from '@/components/TheUser.vue';
import TheNavigation from '@/components/TheNavigation.vue';

const { domain, loadTheme } = useTheme();

export default {
  name: 'App',
  components: {
    TheLogo,
    TheUser,
    TheNavigation,

    TheNoticeVersion: () => import('@/components/TheNoticeVersion.vue'),
  },
  mixins: [
    verifyDataUserMixin,
    loadingMixin,
  ],
  provide() {
    return {
      domain: this.domain,
    };
  },
  data: () => ({
    // control render
    renderHeader: false,
    renderNav: false,
    // router-view só monta depois que handleTheme() termina, pra evitar que
    // a página monte e dispare suas próprias chamadas em paralelo com o
    // carregamento do tema (ver created()).
    bootstrapped: false,

    // control visibility
    retractNavigation: false,

    // navigation
    mouseOverPinState: false,
    mouseOverPinTimeout: null,
    mouseOverTimeout: 350,

    // version
    noticeVersion: false,
    versionRouterUnsub: null,
    pendingVersionReload: false,
  }),
  computed: {
    ...mapState({
      $_screenWidth: (state) => state.app.screenWidth,
      $_retract: (state) => state.retract,
      theme: (state) => state.theme.theme,
      user: (state) => state.user.userInfo,
    }),
    ...mapGetters({
      isMobile: 'app/isMobile',
    }),

    // global
    domain() {
      return domain();
    },
    screenWidth: {
      get() {
        return this.$_screenWidth;
      },
      set(value) {
        this.setScreenWidth(value);
      },
    },
    hasTheme() {
      return !!this.theme.business_info;
    },
    isFullLayoutView() {
      if (!this.$route.name) return false;

      return ![
        'login',
        'recover-password',
        'change-password',
        'video-call',
      ].includes(this.$route.name);
    },

    // loader
    loaderConfig() {
      return {
        color: this.theme?.styles?.color || themeFallback.styles.color,
        opacity: this.hasTheme ? 80 : 100,
        backgroundColor: this.hasTheme ? '#343a40' : '#fff',
        size: 5,
        speed: 2,
        name: 'circular',
        objectbg: '#999793',
      };
    },

    // navigation
    minimized: {
      set(payload) {
        this.setMinimized(payload);
      },
      get() {
        return this.$_retract.minimized;
      },
    },
    pinned: {
      set(payload) {
        this.setPinned(payload);
      },
      get() {
        return this.$_retract.pinned;
      },
    },
    mouseover: {
      set(payload) {
        this.setMouseover(payload);
      },
      get() {
        return this.$_retract.mouseover;
      },
    },
    automatic: {
      set(payload) {
        this.setAutomatic(payload);
      },
      get() {
        return this.$_retract.automatic;
      },
    },
    mouseenter() {
      return !this.isMobile ? 'mouseenter' : null;
    },
    mouseleave() {
      return !this.isMobile ? 'mouseleave' : null;
    },
  },
  watch: {
    user() {
      this.getStatusList();
    },
    retractNavigation(isTrue) {
      if (isTrue) {
        window.addEventListener('click', this.handleClickOutside);
      } else {
        window.removeEventListener('click', this.handleClickOutside);
      }
    },
    $route() {
      if (this.mouseover && !this.minimized && !this.isMobile) this.retract();

      this.renderHeader = this.isFullLayoutView;
      this.renderNav = this.isFullLayoutView;

      this.retractNavigation = false;
    },
  },
  async created() {
    // global
    window.addEventListener('resize', this.resize);
    window.addEventListener('refreshToken', this.handlerRefreshToken);
    window.addEventListener('renderHeader', this.handlerRenderHeader);
    window.addEventListener('renderNav', this.handlerRenderNav);
    window.addEventListener('deployVersionObserved', this.handlerDeployVersionObserved);

    // setting system up
    await this.handleTheme();
    this.bootstrapped = true;
    this.renderHeader = this.isFullLayoutView;
    this.renderNav = this.isFullLayoutView;

    // app version :: initial check + recurrent triggers
    await this.handleVersionCheck({ force: true });
    this.versionRouterUnsub = this.$router.afterEach(() => {
      this.handleVersionCheck();
    });
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  async mounted() {
    this.resize();
    this.toggleBodyClass(true);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize);
    window.removeEventListener('refreshToken', this.handlerRefreshToken);
    window.removeEventListener('renderHeader', this.handlerRenderHeader);
    window.removeEventListener('renderNav', this.handlerRenderNav);
    window.removeEventListener('deployVersionObserved', this.handlerDeployVersionObserved);

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    if (this.versionRouterUnsub) this.versionRouterUnsub();

    this.toggleBodyClass(false);
  },
  methods: {
    ...mapActions({
      setScreenWidth: 'app/SET_SCREEN_WIDTH',
      getStatusList: 'app/SET_STATUSES',
      setToken: 'app/SET_TOKEN',
      setMinimized: 'retract/SET_MINIMIZED',
      setPinned: 'retract/SET_PINNED',
      setMouseover: 'retract/SET_MOUSEOVER',
      setAutomatic: 'retract/SET_AUTOMATIC',
    }),

    // body
    toggleBodyClass(isTrue) {
      const [className] = this.domain.split('.');

      if (isTrue) window.document.body.classList.add(className);
      else window.document.body.classList.remove(className);
    },
    resize() {
      this.screenWidth = parseInt(window.innerWidth, 10);
    },
    setFavicon() {
      const link = document.createElement('link');

      link.setAttribute('rel', 'shortcut icon');
      link.setAttribute('type', 'image/png');
      link.setAttribute('href', this.theme?.styles?.logo?.favicon);

      document.head.appendChild(link);
    },

    // system
    handlerRefreshToken({ detail }) {
      this.setToken(detail);
    },
    handlerRenderHeader({ detail }) {
      this.renderHeader = detail;
    },
    handlerRenderNav({ detail }) {
      this.renderNav = detail;
    },

    // navigation
    handlerToggleRetractNavigation() {
      this.retractNavigation = !this.retractNavigation;
    },
    retract() {
      if (this.isMobile) return;

      let value = !this.minimized;

      if (this.mouseover) value = true;

      this.mouseover = false;
      this.minimized = value;
      this.pinned = value;
    },
    mouseOverRetract(event) {
      clearTimeout(this.automatic);
      this.automatic = setTimeout(() => {
        this.minimized = event;
        this.mouseover = !this.minimized;
      }, this.mouseOverTimeout);
    },
    mouseOverPin(event) {
      clearTimeout(this.mouseOverPinTimeout);
      this.mouseOverPinTimeout = setTimeout(() => {
        this.mouseOverPinState = event;
      }, this.mouseOverTimeout);
    },
    handleClickOutside({ target }) {
      if (target.closest('.layout__content')) {
        this.retractNavigation = false;
      }
    },

    // api
    async handleTheme() {
      this.loading = true;
      try {
        await loadTheme();
      } catch {
        // silent error
      } finally {
        this.loading = false;
        this.setFavicon();
      }
    },

    // version
    async handleVersionCheck({ force = false } = {}) {
      const result = await useAppVersion().validateAppVersion({ force });
      this.actOnVersionResult(result);
    },
    // fed by the X-Deploy-Version-Admin header (see services/api.js) instead of polling
    handlerDeployVersionObserved({ detail }) {
      const result = useAppVersion().reportObservedVersion(detail);
      this.actOnVersionResult(result);
    },
    // capped via useAppVersion().registerAutoReload() — a deploy that never actually
    // converges (e.g. CDN/S3 serving a stale index.html) would otherwise auto-reload forever
    attemptAutoReload() {
      this.pendingVersionReload = false;
      if (useAppVersion().registerAutoReload()) {
        window.location.reload();
        return;
      }
      this.noticeVersion = true;
    },
    actOnVersionResult(result) {
      if (!result?.scheduled) return;

      // only reload a hidden tab — never interrupt one the user is actively on.
      // a visible tab gets the dismissible notice and self-updates next time it's backgrounded
      if (document.visibilityState === 'hidden') {
        this.attemptAutoReload();
        return;
      }

      this.noticeVersion = true;
      this.pendingVersionReload = true;
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'hidden' && this.pendingVersionReload) {
        this.attemptAutoReload();
        return;
      }

      if (document.visibilityState === 'visible') {
        this.handleVersionCheck();
      }
    },
  },
};
</script>

<style lang="scss">
@import './assets/scss/styles.scss';

html,
body {
  margin: 0px !important;
  padding: 0px !important;
  overflow: auto;
}

// analytics
[id*='btn'] * {
  pointer-events: none;
}
</style>

<style lang="scss" scoped>
.layout {
  --header-height: 60px;
  --content-height: calc(100vh - var(--header-height));
  --nav-max-width: 230px;

  position: relative;

  display: grid;
  width: 100vw;
  height: 100vh;
  background-color: $color-primary;

  grid-template-areas: 'logo user' 'nav content';
  grid-template-columns: minmax(auto, var(--nav-max-width)) 1fr;
  grid-template-rows: var(--header-height) 1fr;

  overflow: hidden;

  &.no-header {
    --content-height: 100vh;

    grid-template-rows: 1fr;
    grid-template-areas: 'nav content';
  }

  &.no-nav {
    grid-template-areas: 'logo user' 'content content';
  }

  &.no-header.no-nav {
    grid-template-columns: 1fr;
    grid-template-areas: 'content';
  }

  &.is-mobile {
    --header-height: 50px;

    grid-template-rows: 50px 1fr;
    grid-template-columns: auto 100%;

    .layout__nav {
      min-width: 60vw;
    }

    .layout__content {
      position: relative;

      &.darkenContent {
        padding: 0;
        overflow: hidden;

        &::before {
          content: "";

          position: absolute;
          z-index: 9999;

          display: block;
          width: 100%;
          height: 100%;
          background-color: rgba(#333, 0.6);
        }
      }
    }

    > :deep([class*='content']) {

      $recuo: 1rem;

      padding: 0 $recuo $recuo $recuo;

      > .card {
        margin: $recuo auto;
      }
    }

    :deep(.actions) {
      position: absolute;
      right: 24px;
      top: 24px;
    }
  }

  &:not(.is-mobile) {
    [class*='__logo'] {
      cursor: pointer;
    }
  }

  &__logo,
  &__user {
    height: var(--header-height);
  }

  &__nav,
  &__content {
    height: var(--content-height);
  }

  &__logo {
    grid-area: logo;
  }

  &__user {
    grid-area: user;
  }

  &__nav {
    grid-area: nav;
    overflow: hidden;

    background-color: white;

    &.expand {
      position: absolute;
      z-index: 99;

      width: 230px;

      box-shadow: 5px 0 3px rgba(#333, 0.1);
    }
  }

  &__content {
    grid-area: content;

    padding: 0 2rem 2rem 2rem;
    background-color: $color-light;
    overflow: auto;
    box-shadow: inset 0.5px 0.5px 1px rgba(10, 10, 10, 0.1);

    box-shadow: inset 1px 1px 1px rgba(10, 10, 10, 0.1);

    > .card {
      margin: 30px auto;

      max-width: var(--max-width);
      box-shadow: 0 1px 1px 1px rgba(10, 10, 10, 0.1);
    }
  }

  > .retract {
    $size: 2rem;

    position: absolute;
    top: calc(var(--content-height) - 4rem);
    left: calc(var(--nav-max-width) - 1.25rem);
    margin: 0;
    z-index: 1;

    display: flex;
    width: $size;
    height: $size;
    border-radius: 100%;
    padding: 1rem;
    background: $color-secondary;
    border: 4px solid white;

    align-items: center;
    justify-content: center;
    cursor: pointer;

    i {
      $size: 1.5rem;

      margin-left: $size * 0.15;

      font-size: $size;
      color: white;
    }

    &.rotate {
      transform: rotate(180deg);
    }

    &.expand {
      left: calc(230px - 1.25rem);
      z-index: 99;
    }
  }

  .mouseover-pin {
    $color-accent: rgba(var(--color-secondary-rgb), .5);
    $color-background: $color-secondary;

    &[class*="__nav"],
    &[class*="__logo"] {
      position: relative;

      &::before {
        content: '';

        position: absolute;
        top: 0;
        right: 0;
        z-index: 1;

        display: block;
        width: 2.5px;
        height: 100%;
        background-color: $color-accent;
      }
    }

    &.retract {
      background: $color-background;
      border-color: $color-accent;
    }
  }
}
</style>
